import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUploadCloud, FiShare2, FiUsers, FiGlobe, FiChevronLeft, FiChevronDown, FiX, FiRefreshCw, FiCheck } from 'react-icons/fi';
import { ROUTES } from '../app/routes';
import { upsertMyTool, createShare, getShares, revokeShare, regenerateShare, refreshShare, installToolForAgency, findToolBySourceSkillId, getMyTool, submitPublishRequest, type ShareScope, type MyToolSummary } from '../services/myToolsApi';
import { extractSkillDescription } from '../utils/skillFrontmatter';

interface SkillShareMenuModalProps {
  // The originating Digital Workforce skill's own stable id (SkillLibraryItem.id) —
  // lets this modal find and reuse a my_tools row from a past share instead of
  // minting a duplicate every time it's reopened.
  skillId: string;
  skillName: string;
  skillBody: string;
  isAgencyOwner: boolean;
  onClose: () => void;
}

// Single entry point for everything an owner can do with a custom skill they've
// authored on an agent: publish it (via the normal wizard, prefilled), share a
// personal link to it (Google-Docs style), or push it out to the whole agency.
// Share via Link and Install for Agency both mint an independent copy server-side
// the first time — the agent's live skill is never referenced directly, so editing
// it later never affects anything already published/installed — but reuse the same
// copy (via sourceSkillId) on every subsequent open, rather than minting a fresh one.
const SkillShareMenuModal: React.FC<SkillShareMenuModalProps> = ({ skillId, skillName, skillBody, isAgencyOwner, onClose }) => {
  const navigate = useNavigate();
  const [view, setView] = useState<'menu' | 'link'>('menu');
  const [error, setError] = useState('');

  // Share-via-link sub-view state
  const [scope, setScope] = useState<ShareScope>('agency');
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkResult, setLinkResult] = useState<{ id: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  // The independent "source" my_tools row backing this skill's share — found via
  // sourceSkillId on mount if one already exists, or minted fresh on first Create
  // Link/Install for Agency. Refresh re-pushes the current live skillBody onto this
  // row, then re-syncs the share's copy from it.
  const [sourceToolId, setSourceToolId] = useState<string | null>(null);
  const [refreshed, setRefreshed] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  // Full My Creations record for this skill's listing, once one is found to
  // already exist — drives the same richer menu (in-place Publish, Share with
  // Agency toggle) that ToolShareMenuModal shows for an already-listed tool.
  // A skill that's never been shared before has none of this yet, so it falls
  // back to the simpler menu below (Publish to Marketplace navigates instead).
  const [existingTool, setExistingTool] = useState<MyToolSummary | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  // Load any share already active for this skill, so re-opening "Share via Link"
  // shows the existing link (and lets it be copied/refreshed/revoked) instead of
  // only ever offering to mint a brand-new one on top of it — mirrors
  // AgentShareMenuModal/ToolShareMenuModal's identical behavior.
  useEffect(() => {
    let cancelled = false;
    findToolBySourceSkillId(skillId)
      .then(async existingToolId => {
        if (cancelled || !existingToolId) return;
        setSourceToolId(existingToolId);
        const [shares, tool] = await Promise.all([getShares(existingToolId), getMyTool(existingToolId)]);
        const existing = shares.filter(s => s.isActive).slice(-1)[0];
        if (cancelled) return;
        if (existing) {
          setLinkResult({ id: existing.id, token: existing.token });
          setScope(existing.scope);
        }
        if (tool) {
          setExistingTool(tool);
          setAgencyInstalled(!!tool.isAgencyInstalled);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, [skillId]);

  // Install-for-agency state
  const [installingAgency, setInstallingAgency] = useState(false);
  const [agencyInstalled, setAgencyInstalled] = useState(false);

  const handlePublishToMarketplace = () => {
    navigate(`${ROUTES.MY_TOOLS}?view=create`, {
      state: { prefillSkill: { name: skillName, skillContent: skillBody } },
    });
  };

  const handlePublishExisting = async () => {
    if (!existingTool) return;
    setPublishing(true);
    setError('');
    try {
      await submitPublishRequest(existingTool.id);
      setPublished(true);
    } catch (e: any) {
      setError(e.message === 'already_pending' ? 'This tool already has a pending review request.' : (e.message || 'Failed to submit for review'));
    } finally {
      setPublishing(false);
    }
  };

  // Share with Agency just bumps visibility — but upsertMyTool's UPDATE path
  // overwrites every column unconditionally, so this sends back every field
  // existingTool already carries, not just the changed one.
  const handleShareWithAgency = async () => {
    if (!existingTool) return;
    const updated = { ...existingTool, visibility: 'my_agency' as const };
    setExistingTool(updated);
    onClose();
    await upsertMyTool({
      id: Number(updated.id),
      type: 0,
      name: updated.name,
      category: updated.category,
      icon: updated.icon,
      description: updated.description,
      skills: updated.skills,
      kbFileNames: updated.kbFileNames,
      visibility: 1,
      skillContent: updated.skillContent,
      sourceSkillId: skillId,
    });
  };

  const handleCreateLink = async () => {
    setCreatingLink(true);
    setError('');
    try {
      const toolId = await upsertMyTool({
        id: sourceToolId ? Number(sourceToolId) : undefined,
        type: 0, // ToolType.Skill
        name: skillName.trim(),
        category: 'AI & ML',
        icon: '',
        description: extractSkillDescription(skillBody) || '',
        skills: '',
        kbFileNames: [],
        visibility: 0, // just_me — a share is always an independent draft copy
        skillContent: skillBody.trim(),
        sourceSkillId: skillId,
      });
      setSourceToolId(toolId);
      const share = await createShare(toolId, scope);
      setLinkResult({ id: share.id, token: share.token });
    } catch (e: any) {
      setError(e.message || 'Failed to create share link');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleRefresh = async () => {
    if (!linkResult || !sourceToolId) return;
    setLinkBusy(true);
    setError('');
    try {
      // Push the skill's current live content onto the source row first —
      // refresh_share only re-syncs the share's copy FROM the source, it
      // doesn't know about the agent's live skillBody.
      await upsertMyTool({
        id: Number(sourceToolId),
        type: 0,
        name: skillName.trim(),
        category: 'AI & ML',
        icon: '',
        description: extractSkillDescription(skillBody) || '',
        skills: '',
        kbFileNames: [],
        visibility: 0,
        skillContent: skillBody.trim(),
        sourceSkillId: skillId,
      });
      await refreshShare(linkResult.id);
      setRefreshed(true);
      setTimeout(() => setRefreshed(false), 2500);
    } catch (e: any) {
      setError(e.message || 'Failed to refresh share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const shareUrl = (token: string) => `${window.location.origin}/share/${token}`;

  const handleCopy = () => {
    if (!linkResult) return;
    navigator.clipboard?.writeText(shareUrl(linkResult.token)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    try {
      await revokeShare(linkResult.id);
      setLinkResult(null);
    } catch (e: any) {
      setError(e.message || 'Failed to revoke share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    try {
      const regenerated = await regenerateShare(linkResult.id);
      setLinkResult({ id: regenerated.id, token: regenerated.token });
    } catch (e: any) {
      setError(e.message || 'Failed to regenerate share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleInstallForAgency = async () => {
    setInstallingAgency(true);
    setError('');
    try {
      const toolId = await upsertMyTool({
        id: sourceToolId ? Number(sourceToolId) : undefined,
        type: 0,
        name: skillName.trim(),
        category: 'AI & ML',
        icon: '',
        description: extractSkillDescription(skillBody) || '',
        skills: '',
        kbFileNames: [],
        visibility: 0,
        skillContent: skillBody.trim(),
        sourceSkillId: skillId,
      });
      setSourceToolId(toolId);
      await installToolForAgency(toolId);
      setAgencyInstalled(true);
    } catch (e: any) {
      setError(e.message || 'Failed to install for agency');
    } finally {
      setInstallingAgency(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(6px) saturate(1.2)', WebkitBackdropFilter: 'blur(6px) saturate(1.2)',
      zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface-card, #1a1a1f)',
        backdropFilter: 'blur(24px) saturate(1.3)', WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
        border: '1.5px solid var(--border-glass, rgba(255,255,255,0.1))', borderRadius: 18, padding: 24,
        width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 14,
        boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {view === 'link' && (
            <button onClick={() => setView('menu')} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: 'var(--text-secondary)', display: 'flex',
            }} aria-label="Back">
              <FiChevronLeft size={18} />
            </button>
          )}
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>
            {view === 'menu' ? `Share "${skillName}"` : 'Share via Link'}
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)',
            display: 'flex', padding: 4,
          }} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>

        {error && <p style={{ color: '#f87171', fontSize: 12.5, margin: 0 }}>{error}</p>}

        {view === 'menu' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {existingTool ? (
              <button onClick={handlePublishExisting}
                disabled={publishing || published || existingTool.visibility === 'published' || existingTool.publishStatus === 'pending'}
                style={{ ...menuRowStyle, opacity: publishing ? 0.6 : 1 }}>
                {published || existingTool.publishStatus === 'pending' ? <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} /> : <FiUploadCloud size={18} style={{ color: '#f97316', flexShrink: 0 }} />}
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>
                    {publishing ? 'Publishing…' : existingTool.visibility === 'published' ? 'Published' : (published || existingTool.publishStatus === 'pending') ? 'Submitted for review' : 'Publish'}
                  </div>
                  {!published && existingTool.visibility !== 'published' && existingTool.publishStatus !== 'pending' && (
                    <div style={menuRowSubStyle}>Submit this for a platform admin's approval</div>
                  )}
                </div>
              </button>
            ) : (
              <button onClick={handlePublishToMarketplace} style={menuRowStyle}>
                <FiUploadCloud size={18} style={{ color: '#f97316', flexShrink: 0 }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>Publish to Marketplace</div>
                  <div style={menuRowSubStyle}>Review the details and submit for approval</div>
                </div>
              </button>
            )}

            {existingTool && (
              <button onClick={handleShareWithAgency} disabled={existingTool.visibility !== 'just_me'} style={menuRowStyle}>
                <FiUsers size={18} style={{ color: '#4ade80', flexShrink: 0 }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>{existingTool.visibility === 'just_me' ? 'Share with Agency' : 'Shared with your agency'}</div>
                  {existingTool.visibility === 'just_me' && <div style={menuRowSubStyle}>Makes this visible in your agency's marketplace</div>}
                </div>
              </button>
            )}

            <button onClick={() => setView('link')} style={menuRowStyle}>
              <FiShare2 size={18} style={{ color: '#60a5fa', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={menuRowTitleStyle}>Share via Link</div>
                <div style={menuRowSubStyle}>Anyone with the link installs their own copy</div>
              </div>
            </button>

            {isAgencyOwner && (
              <button onClick={handleInstallForAgency} disabled={installingAgency || agencyInstalled} style={{ ...menuRowStyle, opacity: installingAgency ? 0.6 : 1 }}>
                {agencyInstalled ? <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} /> : <FiUsers size={18} style={{ color: '#4ade80', flexShrink: 0 }} />}
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>
                    {installingAgency ? 'Installing for agency…' : agencyInstalled ? 'Installed for your agency' : 'Install for Agency'}
                  </div>
                  {!agencyInstalled && <div style={menuRowSubStyle}>Install this for everyone in your agency</div>}
                </div>
              </button>
            )}
          </div>
        )}

        {view === 'link' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Anyone who opens the link installs their own independent copy automatically — no separate
              import step. Works on drafts; sharing doesn't go through review.
            </p>

            {loadingExisting && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 12.5, margin: 0 }}>Checking for an existing link…</p>
            )}

            {!loadingExisting && !linkResult && (
              <>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>General access</div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)',
                    }}>
                      {scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}
                    </div>
                    <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                      <button type="button" onClick={() => setScopeMenuOpen(o => !o)} style={dropdownBtnStyle}>
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                          {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                        </span>
                        <FiChevronDown size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                      </button>
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }}>
                        {scope === 'anyone'
                          ? 'With this link, even agents outside your agency install their own copy'
                          : 'Anyone in your agency with this link can install their own copy'}
                      </div>

                      {scopeMenuOpen && (
                        <div style={dropdownMenuStyle}>
                          <button type="button" onClick={() => { setScope('agency'); setScopeMenuOpen(false); }} style={dropdownItemStyle}>
                            <FiUsers size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>Anyone in this agency</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Only people in your agency with the link</div>
                            </div>
                            {scope === 'agency' && <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />}
                          </button>
                          <button type="button" onClick={() => { setScope('anyone'); setScopeMenuOpen(false); }} style={{ ...dropdownItemStyle, borderBottom: 'none' }}>
                            <FiGlobe size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>Anyone with the link</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Even agents outside your agency, with the link</div>
                            </div>
                            {scope === 'anyone' && <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setView('menu')} disabled={creatingLink}>
                    Cancel
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleCreateLink} disabled={creatingLink}>
                    {creatingLink ? <><FiRefreshCw size={12} className="ca-fp-spin" /> Creating…</> : 'Create link'}
                  </button>
                </div>
              </>
            )}

            {linkResult && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)',
                  }}>
                    {scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                      {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>can install their own copy</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input readOnly value={shareUrl(linkResult.token)} className="ca-fp-editor__textarea"
                    style={{ minHeight: 'auto', height: 38, padding: '8px 12px', fontSize: 12, flex: 1 }}
                    onClick={e => (e.target as HTMLInputElement).select()} />
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                </div>
                <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                  Edited the skill since sharing this? <strong>Refresh</strong> updates the link's content in
                  place — same URL, nothing breaks for people who already have it.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" disabled={linkBusy} onClick={handleRefresh}>
                    {linkBusy ? 'Working…' : refreshed ? 'Refreshed!' : 'Refresh'}
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" disabled={linkBusy} onClick={handleRegenerate}>
                    {linkBusy ? 'Working…' : 'Regenerate'}
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" disabled={linkBusy} onClick={handleRevoke}>
                    {linkBusy ? 'Working…' : 'Revoke'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const menuRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
  background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
  borderRadius: 11, cursor: 'pointer', textAlign: 'left', width: '100%',
};
const menuRowTitleStyle: React.CSSProperties = { fontSize: 13.5, fontWeight: 650, color: 'var(--text-primary)' };
const menuRowSubStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 };

const dropdownBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%',
  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
  background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
};
const dropdownMenuStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 10,
  background: 'var(--surface-card, #1a1a1f)', border: '1px solid var(--border-glass)', borderRadius: 10,
  boxShadow: '0 16px 40px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
};
const dropdownItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', width: '100%',
  background: 'none', border: 'none', borderBottom: '1px solid var(--border-glass)', cursor: 'pointer', textAlign: 'left',
};

export default SkillShareMenuModal;
